import React from "react";

// Models
import { InfoCardActionType } from "../../../Models/Enums/InfoCardActionType";
import { InfoCardTargetType } from "../../../Models/Enums/InfoCardTargetType";
import { InfoCardActionDTO } from "../../../Models/DTOs/InfoCardDTO";

// Components
import ButtonPill from "../../Buttons/ButtonPill";

// Helpers
import { buildInternalLink } from "../../../Helpers/InfoCards";

export interface ResolvedInfoCardLink {
    // Internal / relative route, navigated via react-router.
    to?: string;
    // External absolute URL, navigated via an anchor.
    href?: string;
    openInNewTab: boolean;
}

/**
 * Resolves an info-card action to a navigable destination, applying the same
 * internal-vs-URL branching used to render CTA buttons. Returns null when the
 * action can't be safely resolved (missing/invalid target or href).
 */
export function resolveInfoCardActionLink(action?: InfoCardActionDTO): ResolvedInfoCardLink | null {
    if (!action) return null;

    // INTERNAL
    if (action.actionType === InfoCardActionType.Internal) {
        const targetId = Number(action.targetId);
        if (!Number.isFinite(targetId) || targetId <= 0) return null;

        const targetType = action.targetType as InfoCardTargetType;
        if (
            targetType !== InfoCardTargetType.Film &&
            targetType !== InfoCardTargetType.Series &&
            targetType !== InfoCardTargetType.Category
        ) {
            return null;
        }

        return { to: buildInternalLink(targetType, targetId), openInNewTab: !!action.openInNewTab };
    }

    // URL
    if (action.actionType === InfoCardActionType.URL) {
        const href = (action.href ?? "").trim();
        if (!href) return null;

        const isExternal = /^https?:\/\//i.test(href);
        return isExternal
            ? { href, openInNewTab: !!action.openInNewTab }
            : { to: href, openInNewTab: !!action.openInNewTab };
    }

    return null;
}

/**
 * Renders a single info-card action (CTA button). Shared by SliderFeaturedContent
 * and the data-driven popup so both honour the same internal/URL branching rules.
 */
export function renderInfoCardAction(
    action: InfoCardActionDTO,
    key: React.Key,
    onClick?: () => void
): React.ReactNode {
    const label = (action.buttonText ?? "").trim();
    if (!label) return null;

    const link = resolveInfoCardActionLink(action);
    if (!link) return null;

    // External URL — plain anchor (NavLink can't handle absolute urls).
    if (link.href) {
        return (
            <a
                key={key}
                className={`ickonic-button ickonic-button--pill button`}
                href={link.href}
                target={link.openInNewTab ? "_blank" : "_self"}
                rel={link.openInNewTab ? "noopener noreferrer" : undefined}
                onClick={onClick}
            >
                <div className="ickonic-button__inner">
                    <span>{label}</span>
                </div>
            </a>
        );
    }

    // Internal / relative route.
    return (
        <ButtonPill
            key={key}
            label={label}
            link={link.to}
            openInNewWindow={link.openInNewTab}
            onClick={onClick}
        />
    );
}

interface InfoCardActionsProps {
    actions?: InfoCardActionDTO[];
    keyPrefix?: string | number;
    // Fired when any action is clicked (e.g. to dismiss a popup on navigation).
    onActionClick?: () => void;
}

const InfoCardActions: React.FC<InfoCardActionsProps> = ({ actions, keyPrefix = "ic", onActionClick }) => {
    if (!actions || actions.length === 0) return null;

    return (
        <>
            {actions.map((action, j) =>
                renderInfoCardAction(action, `${keyPrefix}-${j}`, onActionClick)
            )}
        </>
    );
};

export default InfoCardActions;
